import React from 'react'

import SensorInsights, { StatusBadge, formatPrice } from './adasInsights'

// Read-only Mechanical section for the client portal. Mirrors AdasSections but
// with no edit/remove affordances. Items are grouped by subcategory (panel.name)
// and each subcategory reuses the shared SensorInsights block — matched to the
// report's existing ADAS insight data by rawSensorId — exactly like the ADAS
// "Diagnostic Scans" category.
const SCAN_BASE_IDS = [
  'pre-repair-scan',
  'in-process-scan',
  'post-repair-scan',
  'pre-and-post-repair-scan',
]

const MechanicalSection = ({ report, allowViewPricing }) => {
  const mechanical = report && report.mechanical
  const panels = (mechanical && mechanical.panels) || []
  const adasServices = (report && report.adas && report.adas.services) || []

  const scanBaseId = (svc) => {
    const opId = svc && svc.operation && svc.operation.id
      ? String(svc.operation.id).toLowerCase()
      : ''
    if (!opId) return null
    return SCAN_BASE_IDS.find(base => opId === base || opId.indexOf(`${base}-`) === 0) || null
  }

  // Relink a seeded mechanical scan row to its ADAS "Diagnostic Scans" data so it
  // renders the SAME insights (QA #26). getSensorInsight/getLinkedSafetySystems
  // key on rawSensorId (adasThink UUID); getSensorDocuments keys on sensorId
  // (slug). A seeded row's own rawSensorId is the scan slug, matching neither —
  // so expose the API-stamped UUID as rawSensorId and carry the slug as sensorId.
  const enrichItem = (item) => {
    if (!item) return item
    if (item.sensorId || item.adasRawSensorId) {
      return { ...item, sensorId: item.sensorId || null, rawSensorId: item.adasRawSensorId || item.rawSensorId }
    }
    const bySlug = adasServices.find(svc => scanBaseId(svc) && scanBaseId(svc) === item.rawSensorId)
    const src = bySlug || (item.sensorName && adasServices.find(svc => svc.sensorName === item.sensorName))
    if (src) {
      return { ...item, sensorId: src.sensorId || item.sensorId, rawSensorId: src.rawSensorId || item.rawSensorId, sensorName: src.sensorName || item.sensorName }
    }
    return item
  }

  // Keep only active items per panel, preserving panel grouping/order.
  const groups = panels
    .map(panel => ({
      name: (panel && panel.name) || 'Mechanical',
      items: ((panel && panel.items) || []).filter(item => item && item.active).map(enrichItem),
    }))
    .filter(group => group.items.length > 0)

  const totalItems = groups.reduce((count, group) => count + group.items.length, 0)

  // Hide the whole section when there is no mechanical data / no active items.
  if (!mechanical || !totalItems) return null

  const rate = mechanical.laborRateMechanical || 0
  const sensorInsights = report.sensorInsights || []
  const collision = !!report.vehicleInvolvedInCollision

  const lineTotal = (item) => (item.hours || 0) * rate
  const mechanicalTotal = groups.reduce((sum, group) => (
    sum + group.items.reduce((groupSum, item) => groupSum + lineTotal(item), 0)
  ), 0)

  const formatHours = (hours) => (
    (hours || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  )

  return (
    <div className='adas-detail__report-card'>
      <div className='adas-detail__report-header'>
        <div className='adas-detail__report-header-left'>
          <h3 className='adas-detail__report-title'>Mechanical</h3>
        </div>
      </div>

      <div className='adas-detail__mech-info-bar'>
        <span className='adas-detail__mech-info-text'>
          Mechanical rate applies to all labor hour calculations in this section
        </span>
        <span className='adas-detail__mech-info-rate'>
          Mechanical Rate: {formatPrice(rate)} /hr
        </span>
      </div>

      <div>
        {groups.map((group, groupIdx) => (
          <div key={`${group.name}-${groupIdx}`}>
            <div className='adas-detail__sensor-header'>{group.name}</div>

            {group.items.map((item, itemIdx) => (
              <div key={item._id || `${group.name}-${itemIdx}`} className='adas-detail__service-row'>
                <div className='adas-detail__service-name'>
                  <span>{item.name || '—'}</span>
                </div>
                <div className='adas-detail__service-status'>
                  <StatusBadge status={item.recommendedStatus} source={item.recommendedSource} />
                </div>
                <div className='adas-detail__mech-labor'>
                  <span className='adas-detail__meta-label'>Labor Hours:</span>
                  <span className='adas-detail__price-value'>{formatHours(item.hours)} hrs</span>
                  {allowViewPricing && (
                    <span className='adas-detail__price-value'>
                      &times; {formatPrice(rate)}/hr = {formatPrice(lineTotal(item))}
                    </span>
                  )}
                </div>
              </div>
            ))}

            <SensorInsights
              sensorName={group.name}
              services={group.items}
              sensorInsights={sensorInsights}
              report={report}
              collision={collision}
              includeCustom
            />
          </div>
        ))}

        {allowViewPricing && (
          <div className='adas-detail__service-row'>
            <div className='adas-detail__service-name'>
              <span className='adas-detail__price-value'>Mechanical Total</span>
            </div>
            <div className='adas-detail__service-status' />
            <div className='adas-detail__mech-labor'>
              <span className='adas-detail__price-value'>{formatPrice(mechanicalTotal)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default MechanicalSection
